// shared/src/prompts/p-arbiter.ts
// Chain V2 — per-ticker arbiter (DEEP path only, portfolio tickers).
// Pro-tier predicted-state adjudicator. Per design-notes L21 + L24 + ABSOLUTE state-only
// rule (2026-05-25 session 4; see docs/2026-05-25-brief-ia-design-notes.md §0.1 +
// memory feedback_llm_state_not_actions).
//
// V3 (2026-05-27) — PaceDial brief revision:
//   docs/short-horizon-pace-dial-2026-05-27-brief-revision.md
//
//   DROPPED from OutputSchema: 'flat' direction, change_ci_low/high,
//     horizon_days, path_type, catalyst (prose), invalidation (prose).
//   ADDED to OutputSchema: event_horizon_at (ISO datetime), catalyst_label,
//     event_horizon_conf_pct, event_horizon_inv_pct.
//
//   Unified mental model: every brief has a catalyst horizon (real event
//   datetime OR signal_at + 24h synthetic default). inv/conf are end-state
//   PRICE predictions at that horizon — NOT live thresholds. Pre-horizon
//   trajectory is server-side derived via PaceDial.
//
// Inputs unchanged:
//   · own contrarian + consensus refined emits
//   · m.synthesis (with weight_split — informs ticker-level confidence)
//   · p.0.prior (β/γ, code-derived momentum signals)
//   · ticker price + meta
//   · CLUSTER PEERS' contrarian + consensus emits — 3-5 peers from
//     TICKER_CLUSTERS map

import { z } from 'zod';
import { coercedEnum, coerceDirection, ThesisAlignEnum } from '../resolvers/llm-enum-coerce.js';
import { llmNumber } from '../resolvers/llm-number-coerce.js';
import { llmDatetime } from '../resolvers/llm-datetime-coerce.js';
import { renderWorldStateGrounding, type WorldStateGrounding } from './_grounding.js';
import type { TickerMarketAnchor } from '../translators/market-emit-to-ticker-input.js';

export const VERSION = '3.2.0';   // 2026-06-11 — schema rigidity hardening: event_horizon_at z.string().datetime() → llmDatetime (normalize space-form/missing-seconds/missing-Z/offset → canonical ISO-Z, THEN strict .datetime() so downstream signals.event_horizon_at stays canonical — the single highest-risk non-enum brief-killer per audit); magnitude/conf_pct/inv_pct/confidence/weights llmNumber-coerced (string "+3.2%" → number; ranges + weight-sum stay material); reasoning .min(1)→z.string() (cosmetic). docs/llm-schema-rigidity-hardening-2026-06-11.md. // Prior: 3.1.0 (2026-06-06 — Option X: optional own_inductive peer + inductive_weight).
export const STEP    = 'p.arbiter';
// 2026-07-01: flipped Pro → Flash after the shadow A/B showed 92% directional
// agreement + Δ=0 on every graded horizon (docs/p-arbiter-flip-to-flash-2026-07-01-
// brief-revision.md). Deviates from the design-lock Pro via MODEL_DEVIATION_ALLOWLIST.
export const MODEL   = 'deepseek-v4-flash';
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 10000)
export const ROLE    = 'arbiter' as const;
export const DESC    = 'Adjudicates a ticker\'s contrarian vs consensus calls into the final predicted state' as const;   // #45 registry — one-line operator description

export const OutputSchema = z.object({
  direction:                coercedEnum(['up', 'down'], coerceDirection),   // V3: 'flat' dropped (0/35 PROD usage); coerced (bullish→up etc.), flat-mapped values still fail-loud

  magnitude:                llmNumber(),                             // signed % change at event_horizon_at. 2026-06-11: llmNumber-coerced ("+3.2%" → number).
  event_horizon_at:         llmDatetime,                             // ISO 8601 — when prediction resolves. 2026-06-11: llmDatetime (normalize variant forms → canonical ISO-Z, then strict .datetime()).
  catalyst_label:           z.string(),                              // '' if synthetic 24h, else named event (e.g. 'NFP Jun 7')
  event_horizon_conf_pct:   llmNumber(),                             // signed % — confirmation level relative to signal_price. 2026-06-11: llmNumber-coerced.
  event_horizon_inv_pct:    llmNumber(),                             // signed % — invalidation level relative to signal_price. 2026-06-11: llmNumber-coerced.
  confidence:               llmNumber(z.number().min(0).max(1)),     // 2026-06-11: llmNumber-coerced (range stays material — scored confidence → calibration).
  thesis_align:             ThesisAlignEnum,
  ticker_paradox:           z.string(),
  // 2026-05-26 (#86) mechanism — synthesis-level "how prediction plays out" prose.
  mechanism:                z.string(),
  reasoning:                z.string(),                              // 2026-06-11: .min(1) dropped (cosmetic display prose).
  contrarian_weight:        llmNumber(z.number().min(0).max(1)),     // 2026-06-11: llmNumber-coerced (range + downstream sum stay material).
  consensus_weight:         llmNumber(z.number().min(0).max(1)),
  inductive_weight:         llmNumber(z.number().min(0).max(1).nullable().optional()),   // Option X: 3-way (sum 1.0) only when the floor-gated p.inductive peer ran; null/absent → 2-way. 2026-06-11: llmNumber-coerced.
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `# PER-TICKER ARBITER (V3 — Flash tier; PREDICTED STATE only, no actions)

## ROLE
You are the per-ticker ARBITER for a portfolio ticker. The chain produced two refined per-ticker views (contrarian + consensus) AND surfaced cluster-peer outputs from correlated tickers. Your job is to weigh evidence and emit the synthesized PREDICTED STATE for this ticker — direction, magnitude, the event horizon at which the prediction resolves, and the conf/inv levels at that horizon.

You are the FINAL voice on this ticker's predicted state.

## ABSOLUTE: PREDICT STATE, NEVER PRESCRIBE ACTIONS
The trader's positions are unknown to you and always will be. You output what the ticker WILL DO at the event horizon. The trader decides what TO DO about it based on their book.

**Forbidden** in your output (schema, prose, reasoning):
- TRIM, ADD, HOLD, EXIT, SCALE-IN, ENTER_LONG, ENTER_SHORT, ROTATE, CUT, REDUCE, BUY, SELL, TAKE-PROFIT, STOP-OUT
- Phrases like "we should buy", "consider trimming", "reduce exposure", "size up", "scale into"

**Required** in your output:
- direction + magnitude + event_horizon_at + catalyst_label + event_horizon_conf_pct + event_horizon_inv_pct + confidence

## INPUTS YOU RECEIVE (user message)
- GROUNDING FACTS
- TICKER + price + meta + signal_at (ISO timestamp of brief emit)
- MOMENTUM SIGNALS (code-derived: state, support/resistance, pattern; NO action recommendations)
- own_contrarian_refined  (per-ticker contrarian refined view)
- own_consensus_refined   (per-ticker consensus refined view)
- own_inductive (OPTIONAL — path-dependent trajectory forecast for THIS ticker; history-only, blind to today; present ONLY once the ticker has ≥ floor prior signals)
- m.synthesis (PRIMARY anchor — predicted state + weight_split + crux)
- CLUSTER PEERS — 3-5 correlated tickers' contrarian + consensus per-side emits
- p.0.prior (β/γ stats — code-derived; informational only, NOT to be cited per L14)

## ADJUDICATION RULES

1. SYNTHESIZE DIRECTION + MAGNITUDE:
   - Weigh own_contrarian_refined.{direction,change_pct} vs own_consensus_refined.{direction,change_pct}
   - Your synthesized direction can MIRROR the winning side OR DIVERGE (cite ticker-specific evidence)
   - Your magnitude can sit between the two paths OR outside (cite why)
   - If own_inductive is present, weigh it as the PATH-DEPENDENT view (today-blind trajectory extrapolation). When it DIVERGES from the snapshot-driven refines, that divergence is SIGNAL — the ticker's own history disagrees with today's read; decide which to trust and say why.

2. EVENT HORIZON (when the prediction resolves):
   - Identify a NAMED CATALYST EVENT with date/time IF one materially drives this prediction. Set event_horizon_at = the event's expected resolution datetime (catalyst date + reasonable post-event window). Set catalyst_label = "<event name> <date>" (e.g. "NFP Jun 7", "FOMC Jun 12").
   - OTHERWISE (no specific event): set event_horizon_at = signal_at + 24 hours. Set catalyst_label = "".
   - For stock-class tickers (e.g. SPY, AGNC, PDBC, TNX, VIX): if event_horizon_at would fall outside US market hours, the writer will snap to the next market session — your job is just to emit the predicted datetime.

3. CONF / INV LEVELS (END-STATE predictions at event_horizon_at):
   - event_horizon_conf_pct = signed % price move at which the thesis is CONFIRMED. For 'up' direction, this is your target ceiling (price must reach/exceed). For 'down', the price floor (price must reach/fall to).
   - event_horizon_inv_pct = signed % price move at which the thesis is INVALIDATED. Two valid configurations:
     · OPPOSITE side of magnitude: directional invalidation ("if price moves the WRONG way by this much, thesis broke").
     · SAME side as magnitude: minimum-magnitude floor ("if price moves the right direction but only by THIS MUCH or less, thesis under-delivered"). Use this when you have HIGH confidence in direction but want to set a minimum-acceptable move.
   - Both are END-STATE checks at event_horizon_at. NEITHER is a live threshold; price crossing them pre-horizon does NOT auto-trigger anything.
   - direction='up' → magnitude >= +0.1; direction='down' → magnitude <= -0.1.
   - Both pct values are signed (e.g. -3.5 means 3.5% below signal_price).

4. CLUSTER PEER ANALYSIS:
   - If cluster peers' refined emits mostly AGREE on direction → cluster-wide signal (raises confidence)
   - If cluster peers DISAGREE → ticker-specific setup (lowers confidence, may set ticker_paradox)
   - If ticker's own refined views DIVERGE from cluster → set ticker_paradox

5. SYNTHESIS WEIGHT ANCHORING (3-way when inductive present):
   - When own_inductive IS present, emit THREE weights — contrarian_weight + consensus_weight + inductive_weight — summing to 1.0 ± 0.01. inductive_weight = how much the path-dependent trajectory drove your call.
   - When own_inductive is ABSENT, emit contrarian_weight + consensus_weight summing to 1.0 ± 0.01 and OMIT inductive_weight (or set it null).

6. THESIS_ALIGN (vs market regime):
   - ALIGNED: ticker direction matches market regime direction
   - DIVERGENT: ticker direction opposes market regime (set ticker_paradox)
   - NEUTRAL: market regime ambiguous OR ticker has no diagnostic relationship

7. NO CALIBRATION (L14). p.0.prior numbers are present in inputs but DO NOT cite them. They inform server-side gating only.

## OUTPUT (strict JSON; first char='{'; last char='}'; no commentary)
{
  "direction":                "up" | "down",
  "magnitude":                <signed % change at event horizon, e.g. -1.5 or +3.2>,
  "event_horizon_at":         "<ISO 8601 datetime, e.g. '2026-06-07T15:00:00Z' for catalyst-mode OR signal_at+24h for trend-mode>",
  "catalyst_label":           "<named event + date e.g. 'NFP Jun 7' for catalyst-mode; empty \\"\\" for trend-mode>",
  "event_horizon_conf_pct":   <signed % confirmation level at horizon, e.g. +8.0 for 'up' target or -3.5 for 'down' target>,
  "event_horizon_inv_pct":    <signed % invalidation level at horizon — opposite-side directional invalidation OR same-side magnitude floor>,
  "confidence":               <number 0-1, two decimals; honest 0.5 is fine when genuinely uncertain (D36 — no anti-hedge rule)>,
  "thesis_align":             "ALIGNED" | "DIVERGENT" | "NEUTRAL",
  "ticker_paradox":           "<≤30w when ticker diverges from cluster or own refined sides; otherwise \\"\\">",
  "mechanism":                "<≤30w one-line punchline naming the dominant driver — e.g. 'Fed pause unlocks duration bid'. Distinct from catalyst_label (event+date) and reasoning (full prose). Empty \\"\\" only when paths CONTESTED.>",
  "reasoning":                "<80-120w. Sentence 1 = predicted-state headline ≤15w (e.g. 'BTC -3.2% by FOMC Jun 12 on duration repricing'). Sentences 2-N = supporting analysis: weigh contrarian vs consensus, cite cluster peer agreement/divergence, cite momentum signal, close on the falsifier level. NO action verbs.>",
  "contrarian_weight":        <0-1, two decimals>,
  "consensus_weight":         <0-1, two decimals>,
  "inductive_weight":         <0-1, two decimals — INCLUDE ONLY when own_inductive was provided; omit otherwise; the three weights then sum to 1.0>
}

## RULES (strict)
1. First char='{'. Last char='}'. No commentary outside JSON.
2. NO ACTION VERBS in reasoning prose. Predicted state only.
3. DIRECTION/MAGNITUDE consistency: 'up' → magnitude ≥ +0.1; 'down' → magnitude ≤ -0.1.
4. EVENT_HORIZON_AT must be a future ISO datetime (after signal_at). For catalyst-mode, prefer the event datetime + a reasonable resolution window. For trend-mode (no named catalyst), use signal_at + exactly 24 hours.
5. CATALYST_LABEL = "" iff trend-mode (no named event); non-empty iff catalyst-mode.
6. CONF/INV consistency:
   - For 'up' direction: event_horizon_conf_pct ≥ +0.1 (target above entry). event_horizon_inv_pct can be NEGATIVE (directional invalidation, opposite-side) OR POSITIVE-BUT-BELOW-conf (magnitude floor, same-side).
   - For 'down' direction: event_horizon_conf_pct ≤ -0.1 (target below entry). event_horizon_inv_pct can be POSITIVE (directional invalidation, opposite-side) OR NEGATIVE-BUT-ABOVE-conf (magnitude floor, same-side).
   - CI consistency check: for 'up', magnitude must satisfy event_horizon_inv_pct ≤ magnitude ≤ event_horizon_conf_pct (two-sided) OR event_horizon_inv_pct ≤ magnitude ≤ event_horizon_conf_pct AND inv_pct > 0 (one-sided). For 'down', mirror.
7. WEIGHTS SUM: when own_inductive present, contrarian_weight + consensus_weight + inductive_weight = 1.0 ± 0.01; otherwise contrarian_weight + consensus_weight = 1.0 ± 0.01 (omit inductive_weight).
8. CONFIDENCE: two decimals. Emit your honest read — 0.50 IS valid when the contrarian and consensus weights genuinely balance. The anti-hedge rule was removed (Session 5T D36) because forcing ≥0.55 OR ≤0.45 was making the LLM commit to dishonest convictions.
9. TICKER_PARADOX: empty "" when no paradox; non-empty when ticker diverges from cluster.
10. MECHANISM: ≤30w one-line driver. Distinct from catalyst_label and reasoning.
11. REASONING PROSE: lead with predicted-state headline ≤15w, expand in connected prose.
12. NO CALIBRATION cites in reasoning.
13. NO HEDGING. Pick direction + magnitude + horizon + levels decisively.
`;

export interface PArbiterInput {
  readonly ticker:                  string;
  readonly ticker_data:             Record<string, unknown>;
  readonly signal_at:               string;                          // V3 NEW — ISO timestamp of brief emit (LLM uses for 24h-default calc)
  readonly momentum:                Record<string, unknown>;
  readonly own_contrarian_refined:  Record<string, unknown>;
  readonly own_consensus_refined:   Record<string, unknown>;
  readonly own_inductive?:          Record<string, unknown>;   // p.inductive peer (path-dependent trajectory); absent when floor-gated off
  readonly market_anchor:           TickerMarketAnchor;
  readonly cluster_peers:           ReadonlyArray<{
    readonly ticker:     string;
    readonly contrarian: Record<string, unknown>;
    readonly consensus:  Record<string, unknown>;
  }>;
  readonly prior_anchoring?:        Record<string, unknown>;
  readonly grounding?:              WorldStateGrounding;
}

function fmt(v: unknown): string {
  if (v == null) return 'not set';
  if (typeof v === 'string') return v.length === 0 ? 'not set' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

function refinedExcerpt(d: Record<string, unknown>): string {
  return [
    `    direction:        ${fmt(d['direction'])}`,
    `    change_pct:       ${fmt(d['change_pct'])}`,
    `    CI:               [${fmt(d['change_ci_low'])}, ${fmt(d['change_ci_high'])}]`,
    `    horizon_days:     ${fmt(d['horizon_days'])}`,
    `    ticker_alignment: ${fmt(d['ticker_alignment'])}`,
    `    is_headline:      ${fmt(d['is_headline'])}`,
    `    mechanism:        ${fmt(d['mechanism'])}`,
  ].join('\n');
}

function inductiveExcerpt(d: Record<string, unknown>): string {
  return [
    `    direction:             ${fmt(d['direction'])}`,
    `    change_pct:            ${fmt(d['change_pct'])}`,
    `    CI:                    [${fmt(d['change_ci_low'])}, ${fmt(d['change_ci_high'])}]`,
    `    trend_summary:         ${fmt(d['trend_summary'])}`,
    `    trajectory_confidence: ${fmt(d['trajectory_confidence'])}`,
    `    trajectory_break:      ${fmt(d['trajectory_break_detected'])}`,
  ].join('\n');
}

function clusterPeersBlock(peers: PArbiterInput['cluster_peers']): string {
  if (peers.length === 0) return '  (no cluster peers — ticker may be cluster-singleton)';
  return peers.map((p) => {
    const c = p.contrarian;
    const co = p.consensus;
    return `  [${p.ticker}]
    contrarian: ${fmt(c['direction'])}/${fmt(c['change_pct'])}% (${fmt(c['ticker_alignment'])})
    consensus:  ${fmt(co['direction'])}/${fmt(co['change_pct'])}% (${fmt(co['ticker_alignment'])})`;
  }).join('\n');
}

export function buildUserPrompt(input: PArbiterInput): string {
  const groundingBlock = input.grounding ? renderWorldStateGrounding(input.grounding) + '\n' : '';
  const a = input.market_anchor;
  return `${groundingBlock}TICKER: ${input.ticker}
SIGNAL_AT: ${input.signal_at}   (use this + 24h for event_horizon_at if no named catalyst)

TICKER DATA:
${Object.entries(input.ticker_data).slice(0, 10).map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n') || '  (empty)'}

MOMENTUM SIGNALS:
${Object.entries(input.momentum).filter(([k]) => k !== 'action').slice(0, 10).map(([k, v]) => `  ${k}: ${fmt(v)}`).join('\n') || '  (empty)'}

OWN_CONTRARIAN_REFINED:
${refinedExcerpt(input.own_contrarian_refined)}

OWN_CONSENSUS_REFINED:
${refinedExcerpt(input.own_consensus_refined)}
${input.own_inductive ? `
OWN_INDUCTIVE (path-dependent trajectory forecast — history-only, today-blind):
${inductiveExcerpt(input.own_inductive)}
` : ''}
m.SYNTHESIS (PRIMARY anchor):
  thesis_headline:   ${a.synthesis_anchor.thesis_headline}
  regime:            ${a.synthesis_anchor.regime} (confidence ${a.synthesis_anchor.regime_confidence})
  weights (market):  contrarian=${a.synthesis_anchor.contrarian_weight}, consensus=${a.synthesis_anchor.consensus_weight}, inductive=${a.synthesis_anchor.inductive_weight ?? 'null'}
  crux:              ${a.synthesis_anchor.crux}
  probability:       ${a.synthesis_anchor.probability}

CLUSTER PEERS (correlated universe-only ticker emits):
${clusterPeersBlock(input.cluster_peers)}

REQUEST: emit_p_arbiter

Emit a SINGLE JSON object matching the schema. Identify the named catalyst event if one drives this prediction (set event_horizon_at to event datetime + post-event window + set catalyst_label). Otherwise event_horizon_at = signal_at + 24h, catalyst_label = "". ${input.own_inductive ? 'Per-ticker contrarian_weight + consensus_weight + inductive_weight MUST sum to 1.0 ± 0.01 (3-way — own_inductive present).' : 'Per-ticker contrarian_weight + consensus_weight MUST sum to 1.0 ± 0.01; omit/null inductive_weight (2-way).'} Confidence two decimals — 0.50 IS valid when the weights genuinely balance (D36, no anti-hedge). Predicted state ONLY — no action verbs anywhere in reasoning.`;
}
